import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Drawer, IconButton, Tooltip, makeStyles } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import SydButton from '../../../../commonDesign/Button'
import { useToast } from './ToastContext'

const useStyles = makeStyles(() => ({
  paper: {
    width: '480px',
    maxWidth: '95vw'
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 24px 14px',
    borderBottom: '1px solid var(--color-neutral-200)'
  },
  title: {
    fontSize: 'var(--text-size-lg, 18px)',
    fontWeight: 600,
    color: 'var(--color-neutral-900)'
  },
  subtitle: {
    fontSize: 'var(--text-size-xs)',
    color: 'var(--color-neutral-400)',
    marginTop: '2px',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    fontWeight: 500
  },
  body: {
    padding: '20px 24px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    overflowY: 'auto',
    flex: 1
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  label: {
    fontSize: '11px',
    fontWeight: 700,
    color: 'var(--color-neutral-500)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  required: {
    color: 'var(--color-danger-600, #dc2626)',
    marginLeft: '3px'
  },
  input: {
    padding: '7px 10px',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: 'var(--text-size-sm)',
    outline: 'none',
    backgroundColor: 'white',
    color: 'var(--color-neutral-900)',
    fontFamily: 'inherit',
    '&:focus': {
      borderColor: 'var(--color-primary-400)',
      boxShadow: '0 0 0 2px var(--color-primary-100)'
    },
    '&:disabled': {
      backgroundColor: 'var(--color-neutral-050)',
      color: 'var(--color-neutral-500)'
    }
  },
  textarea: {
    minHeight: '72px',
    resize: 'vertical'
  },
  checkboxRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  checkbox: {
    accentColor: 'var(--color-primary-600)',
    width: '15px',
    height: '15px',
    cursor: 'pointer'
  },
  checkboxLabel: {
    fontSize: 'var(--text-size-sm)',
    color: 'var(--color-neutral-800)',
    cursor: 'pointer'
  },
  fieldError: {
    fontSize: '11px',
    color: 'var(--color-danger-600, #dc2626)'
  },
  placeholderText: {
    fontSize: '10px',
    color: 'var(--color-neutral-400)'
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
    padding: '14px 24px',
    borderTop: '1px solid var(--color-neutral-200)'
  }
}))

const initialValues = (fields, record) => {
  const out = {}
  for (const f of fields) {
    if (record && record[f.name] != null) {
      out[f.name] = record[f.name]
    } else if (f.defaultValue !== undefined) {
      out[f.name] = f.defaultValue
    } else if (f.type === 'checkbox') {
      out[f.name] = false
    } else {
      out[f.name] = ''
    }
  }
  return out
}

const buildPayload = (fields, values, isEdit) => {
  const payload = {}
  for (const f of fields) {
    if (f.readOnlyOnEdit && isEdit) continue
    const raw = values[f.name]
    if (f.type === 'checkbox') {
      payload[f.name] = !!raw
      continue
    }
    if (f.type === 'number') {
      if (raw === '' || raw === null || raw === undefined) continue
      const n = Number(raw)
      if (!Number.isNaN(n)) payload[f.name] = n
      continue
    }
    if (f.type === 'select') {
      if (raw === null || raw === '') {
        if (f.allowEmpty) payload[f.name] = null
        continue
      }
      payload[f.name] = raw
      continue
    }
    // text / textarea: send the trimmed value, or (on edit) null to clear an
    // optional field. On create we just omit the field and let the backend
    // default it. Required fields are caught by validate() before we get here.
    if (typeof raw === 'string') {
      const trimmed = raw.trim()
      if (trimmed) {
        payload[f.name] = trimmed
      } else if (isEdit && !f.required) {
        payload[f.name] = null
      }
    } else if (raw != null) {
      payload[f.name] = raw
    }
  }
  return payload
}

const validate = (fields, values, isEdit) => {
  const errors = {}
  for (const f of fields) {
    if (f.type === 'checkbox') continue
    if (f.readOnlyOnEdit && isEdit) continue
    const v = values[f.name]
    const isBlank = v === '' || v == null || (typeof v === 'string' && !v.trim())

    if (f.required && isBlank) {
      errors[f.name] = `${f.label} is required.`
      continue
    }
    if (f.type === 'number' && !isBlank) {
      const n = Number(v)
      if (Number.isNaN(n)) {
        errors[f.name] = `${f.label} must be a number.`
      } else if (f.min != null && n < f.min) {
        errors[f.name] = `${f.label} must be at least ${f.min}.`
      } else if (f.max != null && n > f.max) {
        errors[f.name] = `${f.label} must be at most ${f.max}.`
      }
    }
    if (f.pattern && !isBlank && typeof v === 'string' && !f.pattern.test(v.trim())) {
      errors[f.name] = f.patternMessage || `${f.label} is not in the expected format.`
    }
  }
  return errors
}

function RecordDrawer ({
  open,
  onClose,
  title,
  entityLabel,
  fields,
  options,
  record,
  onInsert,
  onUpdate,
  isMutating
}) {
  const classes = useStyles()
  const { showError } = useToast()
  const isEdit = record != null
  const [values, setValues] = useState(() => initialValues(fields, record))
  const [errors, setErrors] = useState({})

  useEffect(() => {
    if (open) {
      setValues(initialValues(fields, record))
      setErrors({})
    }
  }, [open, fields, record])

  const setField = useCallback((name, value) => {
    setValues(prev => ({ ...prev, [name]: value }))
    setErrors(prev => {
      if (!prev[name]) return prev
      const next = { ...prev }
      delete next[name]
      return next
    })
  }, [])

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault()
    if (isMutating) return
    const errs = validate(fields, values, isEdit)
    if (Object.keys(errs).length > 0) {
      setErrors(errs)
      return
    }
    try {
      const payload = buildPayload(fields, values, isEdit)
      if (isEdit) {
        await onUpdate(payload)
      } else {
        await onInsert(payload)
      }
      onClose()
    } catch (err) {
      showError(err, 'Save failed')
    }
  }, [fields, values, isEdit, isMutating, onInsert, onUpdate, onClose, showError])

  /* eslint-disable react/prop-types */
  const renderInput = (field) => {
    const value = values[field.name]
    const disabled = field.readOnlyOnEdit && isEdit

    if (field.type === 'checkbox') {
      return (
        <div className={classes.checkboxRow}>
          <input
            id={`f-${field.name}`}
            type='checkbox'
            className={classes.checkbox}
            checked={!!value}
            onChange={(e) => setField(field.name, e.target.checked)}
          />
          <label htmlFor={`f-${field.name}`} className={classes.checkboxLabel}>
            {field.label}
          </label>
        </div>
      )
    }

    if (field.type === 'select') {
      const opts = options[field.optionsKey] || []
      return (
        <select
          className={classes.input}
          value={value ?? ''}
          onChange={(e) => setField(field.name, e.target.value || (field.allowEmpty ? null : ''))}
          disabled={disabled}
        >
          <option value=''>{field.allowEmpty ? '— none —' : 'Select…'}</option>
          {opts.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
        </select>
      )
    }

    if (field.type === 'textarea') {
      return (
        <textarea
          className={`${classes.input} ${classes.textarea}`}
          rows={field.rows || 3}
          value={value ?? ''}
          onChange={(e) => setField(field.name, e.target.value)}
          placeholder={field.placeholder}
          disabled={disabled}
        />
      )
    }

    return (
      <input
        type={field.type === 'number' ? 'number' : 'text'}
        step={field.step}
        min={field.min}
        max={field.max}
        maxLength={field.maxLength}
        className={classes.input}
        value={value ?? ''}
        onChange={(e) => setField(field.name, e.target.value)}
        placeholder={field.placeholder}
        disabled={disabled}
      />
    )
  }
  /* eslint-enable react/prop-types */

  return (
    <Drawer anchor='right' open={open} onClose={onClose} classes={{ paper: classes.paper }}>
      <form className={classes.root} onSubmit={handleSubmit}>
        <div className={classes.header}>
          <div>
            <div className={classes.title}>{title}</div>
            <div className={classes.subtitle}>{isEdit ? 'Edit' : 'Create'} · {entityLabel}</div>
          </div>
          <Tooltip title='Close' arrow>
            <IconButton size='small' onClick={onClose} type='button'>
              <CloseIcon fontSize='small' />
            </IconButton>
          </Tooltip>
        </div>

        <div className={classes.body}>
          {fields.map(field => (
            <div key={field.name} className={classes.field}>
              {field.type !== 'checkbox' && (
                <div className={classes.label}>
                  {field.label}
                  {field.required && <span className={classes.required}>*</span>}
                </div>
              )}
              {renderInput(field)}
              {errors[field.name] && (
                <div className={classes.fieldError}>{errors[field.name]}</div>
              )}
              {!errors[field.name] && field.placeholder && field.type !== 'text' && field.type !== 'textarea' && (
                <div className={classes.placeholderText}>{field.placeholder}</div>
              )}
            </div>
          ))}
        </div>

        <div className={classes.footer}>
          <SydButton size='sm' variant='secondary' onClick={onClose} type='button' disabled={isMutating}>
            Cancel
          </SydButton>
          <SydButton size='sm' variant='primary' type='submit' processing={isMutating}>
            {isEdit ? 'Save' : 'Create'}
          </SydButton>
        </div>
      </form>
    </Drawer>
  )
}

RecordDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  entityLabel: PropTypes.string.isRequired,
  fields: PropTypes.array.isRequired,
  options: PropTypes.object,
  record: PropTypes.object,
  onInsert: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  isMutating: PropTypes.bool
}

RecordDrawer.defaultProps = {
  options: {},
  record: null,
  isMutating: false
}

export default RecordDrawer
